import { zodResolver } from "@hookform/resolvers/zod"
import * as Sentry from "@sentry/react"
import { Button } from "design-system/atoms/Button"
import { SheetFooter } from "design-system/atoms/Sheet"
import { Form } from "design-system/organisms/Form"
import config from "lib/config"
import { getErrorTranslations } from "lib/i18n"
import { ensure, isFilled } from "lopermut"
import { useKindBadge } from "modules/capabilities/hooks/useKindBadge"
import { useCallback, useMemo, useState } from "react"
import { useForm, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesMarketplaceResponseDataSchema,
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityKind,
  CapabilityResponseDataSchema,
  ConversationFlowEnum,
  GeneratedRequirement,
  InstructionCapabilityConfigSchema,
  McpCapabilityConfigSchema,
  WebhookBodyFormat,
  WebhookCapabilityConfigSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilitiesDictionary } from "../../hooks/useCapabilitiesDictionary"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityActivationMutation } from "../../hooks/useUpdateCapabilityActivationMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CapabilityActivationSettings } from "../CapabilityActivationSettings"
import { CapabilitySheetLayout } from "../CapabilitySheetLayout"
import { InstructionCapabilityForm } from "../CreateCustomCapability/InstructionCapabilityForm"
import MCPCapabilityForm from "../CreateCustomCapability/MCPCapabilityForm"
import { WebhookCapabilityForm } from "../CreateCustomCapability/WebhookCapabilityForm"

type Props<Mode extends "install" | "configure"> = {
  capability: Mode extends "install"
    ? z.infer<typeof CapabilitiesMarketplaceResponseDataSchema>[number]
    : z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode: Mode
  open?: boolean
  onOpenChange: (open: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  kind?: "webhook" | "instruction" | "mcp"
}

function getDefaultValues(
  kind: "webhook" | "instruction" | "mcp",
  initialConfig?: z.infer<
    | typeof WebhookCapabilityConfigSchema
    | typeof InstructionCapabilityConfigSchema
    | typeof McpCapabilityConfigSchema
  >,
) {
  if (kind === "webhook") {
    const webhookConfig = initialConfig as z.infer<typeof WebhookCapabilityConfigSchema> | undefined
    return {
      name: webhookConfig?.name || "",
      url: webhookConfig?.url || "",
      method: webhookConfig?.method || ("GET" as const),
      activationPrompt: webhookConfig?.activationPrompt || "",
      dataToExtract: webhookConfig?.dataToExtract || [],
      webhookHeaders: webhookConfig?.webhookHeaders || [],
      webhookBody: webhookConfig?.webhookBody || "{}",
      webhookBodyFormat: webhookConfig?.webhookBodyFormat || WebhookBodyFormat.json,
      agentPrompt: webhookConfig?.agentPrompt || "",
      useCache: webhookConfig?.useCache ?? true,
      testResponseBody: webhookConfig?.testResponseBody || null,
      executeOnlyOnce: webhookConfig?.executeOnlyOnce ?? false,
      mode: webhookConfig?.mode || ("single" as const),
      steps: webhookConfig?.steps || [],
    }
  }
  if (kind === "mcp") {
    const mcpConfig = initialConfig as z.infer<typeof McpCapabilityConfigSchema> | undefined
    return {
      name: mcpConfig?.name || "",
      transportType: mcpConfig?.transportType || ("sse" as const),
      serverUrl: mcpConfig?.serverUrl || "",
      authConfig: mcpConfig?.authConfig || {
        type: "none" as const,
      },
      tools: mcpConfig?.tools || [],
      connectionTimeoutMs: mcpConfig?.connectionTimeoutMs?.toString() || "10000",
      executionTimeoutMs: mcpConfig?.executionTimeoutMs?.toString() || "15000",
    }
  }
  const instructionConfig = initialConfig as
    z.infer<typeof InstructionCapabilityConfigSchema> | undefined
  return {
    name: instructionConfig?.name || "",
    activationPrompt: instructionConfig?.activationPrompt || "",
    dataToExtract: instructionConfig?.dataToExtract || [],
    agentPrompt: instructionConfig?.agentPrompt || "",
    executeOnlyOnce: instructionConfig?.executeOnlyOnce ?? false,
  }
}

export function CapabilityCustomDialog<Mode extends "install" | "configure">({
  capability,
  mode,
  open = true,
  onOpenChange,
  onConfigure,
  kind,
}: Props<Mode>) {
  const { t } = useTranslation()
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const { name, detailedDescription, description } =
    capabilitiesDictionary[capability.identifier as keyof typeof capabilitiesDictionary]
  const getBadge = useKindBadge()

  const [isFormPending, setIsFormPending] = useState(false)

  const [campaignAssignation, setCampaignAssignation] = useState<CapabilityCampaignAssignation>(
    mode === "configure" && "campaignAssignation" in capability
      ? capability.campaignAssignation
      : CapabilityCampaignAssignation.AllCampaigns,
  )
  const [campaignFlow, setCampaignFlow] = useState<keyof typeof ConversationFlowEnum>(
    mode === "configure" && "campaignFlow" in capability && capability.campaignFlow
      ? capability.campaignFlow
      : ConversationFlowEnum.Inbound,
  )
  const [campaignIds, setCampaignIds] = useState<string[]>(
    mode === "configure" && "campaignIds" in capability ? capability.campaignIds : [],
  )

  const { mutateAsync: installCapability } = useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig } = useUpdateCapabilityConfigMutation()
  const { mutateAsync: updateActivation } = useUpdateCapabilityActivationMutation()

  const inferredKind = useMemo(() => {
    if (kind) return kind

    switch (capability.kind) {
      case CapabilityKind.Instruction:
        return "instruction" as const
      case CapabilityKind.MCP:
        return "mcp" as const
      case CapabilityKind.Webhook:
      default:
        return "webhook" as const
    }
  }, [kind, capability.kind])

  const schema = useMemo(() => {
    switch (inferredKind) {
      case "webhook":
        return WebhookCapabilityConfigSchema
      case "instruction":
        return InstructionCapabilityConfigSchema
      case "mcp":
        return McpCapabilityConfigSchema
    }
  }, [inferredKind])

  const initialConfig = useMemo(() => {
    if (mode === "configure" && "config" in capability && capability.config) {
      const parsedConfig = schema.safeParse(capability.config)
      if (parsedConfig.success) {
        return parsedConfig.data
      }
      const errorData = {
        capabilityId: capability.id,
        config: capability.config,
        errors: parsedConfig.error.issues,
      }
      if (config.isDevelopment) {
        console.error(`Failed to parse ${inferredKind} capability config`, errorData)
      }
      Sentry.withScope((scope) => {
        scope.setExtra("errorData", JSON.stringify(errorData))
        Sentry.captureException(new Error(`Failed to parse ${inferredKind} capability config`))
      })
    }
    return undefined
  }, [mode, capability, schema, inferredKind])

  const initialInstructionRequirements = useMemo<GeneratedRequirement[]>(() => {
    if (mode !== "configure") return []
    const installedCapability = capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]
    return (installedCapability.requirements ?? []).map(
      ({ rule, description: ruleDescription }) => ({
        rule,
        description: ruleDescription,
      }),
    )
  }, [mode, capability])

  const [instructionRequirements, setInstructionRequirements] = useState(
    initialInstructionRequirements,
  )
  const [requirementsAgentPrompt, setRequirementsAgentPrompt] = useState<string | null>(() => {
    if (initialInstructionRequirements.length === 0 || inferredKind !== "instruction") return null
    const parsed = InstructionCapabilityConfigSchema.safeParse(initialConfig)
    return parsed.success ? parsed.data.agentPrompt.trim() : null
  })

  const form = useForm<
    | z.infer<typeof WebhookCapabilityConfigSchema>
    | z.infer<typeof InstructionCapabilityConfigSchema>
    | z.infer<typeof McpCapabilityConfigSchema>
  >({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: getDefaultValues(inferredKind, initialConfig),
  })

  const onClose = useCallback(() => onOpenChange(false), [onOpenChange])

  const handleConfigure = useCallback(
    (installed: z.infer<typeof CapabilityResponseDataSchema>) => {
      if (mode === "install") {
        if (onConfigure) {
          onConfigure(installed)
        } else {
          onClose()
        }
      }
    },
    [mode, onConfigure, onClose],
  )

  const handleFormSubmit = useCallback(
    async (values: z.infer<typeof schema>) => {
      if (isFormPending) return

      setIsFormPending(true)

      try {
        let config: z.infer<typeof schema>

        switch (inferredKind) {
          case "webhook":
            const webhookValues = values as z.infer<typeof WebhookCapabilityConfigSchema>
            config = {
              name: webhookValues.name.trim(),
              method: webhookValues.method,
              activationPrompt: webhookValues.activationPrompt.trim(),
              dataToExtract: webhookValues.dataToExtract,
              url: webhookValues.url?.trim() || undefined,
              webhookHeaders: webhookValues.webhookHeaders,
              webhookBody: webhookValues.webhookBody,
              webhookBodyFormat: webhookValues.webhookBodyFormat,
              agentPrompt: webhookValues.agentPrompt.trim(),
              useCache: webhookValues.useCache,
              testResponseBody: webhookValues.testResponseBody,
              executeOnlyOnce: webhookValues.executeOnlyOnce,
              mode: webhookValues.mode,
              steps: webhookValues.steps,
            } as z.infer<typeof schema>
            break

          case "instruction":
            const instructionValues = values as z.infer<typeof InstructionCapabilityConfigSchema>
            config = {
              name: instructionValues.name.trim(),
              activationPrompt: instructionValues.activationPrompt.trim(),
              dataToExtract: instructionValues.dataToExtract,
              agentPrompt: instructionValues.agentPrompt.trim(),
              executeOnlyOnce: instructionValues.executeOnlyOnce,
            } as z.infer<typeof schema>
            break

          case "mcp":
            const mcpValues = values as z.infer<typeof McpCapabilityConfigSchema>
            config = {
              name: mcpValues.name.trim(),
              transportType: mcpValues.transportType,
              serverUrl: mcpValues.serverUrl.trim(),
              authConfig: mcpValues.authConfig,
              tools: mcpValues.tools,
              connectionTimeoutMs: mcpValues.connectionTimeoutMs,
              executionTimeoutMs: mcpValues.executionTimeoutMs,
            } as z.infer<typeof schema>
            break
        }

        const requirementsToSave = getInstructionRequirementsForSave({
          inferredKind,
          config,
          requirements: instructionRequirements,
          requirementsAgentPrompt,
        })

        switch (mode) {
          case "configure":
            await updateCapabilityConfig({
              id: ensure("id" in capability ? capability.id : undefined),
              config,
              ...(requirementsToSave ? { requirements: requirementsToSave } : {}),
            })
            await updateActivation({
              id: ensure("id" in capability ? capability.id : undefined),
              campaignAssignation,
              campaignIds,
              campaignFlow,
            })
            toast.success(t("capabilities.activation.success"))
            onClose()

            break

          case "install":
            const installed = await installCapability({
              identifier: capability.identifier,
              config,
              campaignAssignation,
              campaignIds,
              campaignFlow,
              ...(requirementsToSave ? { requirements: requirementsToSave } : {}),
            })
            toast.success(
              t("capabilities.list.install.success", {
                capability: t(`capabilities.catalog.${capability.identifier}.name`),
              }),
            )
            handleConfigure(installed)

            break
        }
      } finally {
        setIsFormPending(false)
      }
    },
    [
      isFormPending,
      inferredKind,
      mode,
      capability,
      updateCapabilityConfig,
      updateActivation,
      campaignAssignation,
      campaignIds,
      campaignFlow,
      t,
      installCapability,
      handleConfigure,
      onClose,
      instructionRequirements,
      requirementsAgentPrompt,
    ],
  )

  const capabilityName = useMemo(() => {
    let targetKind
    switch (inferredKind) {
      case "webhook":
        targetKind = CapabilityKind.Webhook
        break
      case "mcp":
        targetKind = CapabilityKind.MCP
        break
      case "instruction":
        targetKind = CapabilityKind.Instruction
        break
    }

    if (
      "config" in capability &&
      capability.config &&
      "name" in capability.config &&
      capability.kind === targetKind &&
      typeof capability.config.name === "string" &&
      isFilled(capability.config.name)
    ) {
      return capability.config?.name
    }

    return name
  }, [capability, name, inferredKind])

  const dialogTitle =
    mode === "configure"
      ? t("capabilities.activation.editTitle", { name: capabilityName })
      : t("capabilities.list.install.dialog.title", { name: capabilityName })

  const dialogDescription = detailedDescription || description

  const activationSettings = (
    <CapabilityActivationSettings
      campaignAssignation={campaignAssignation}
      setCampaignAssignation={setCampaignAssignation}
      campaignIds={campaignIds}
      setCampaignIds={setCampaignIds}
      campaignFlow={campaignFlow}
      setCampaignFlow={setCampaignFlow}
    />
  )

  const renderContent = () => {
    switch (inferredKind) {
      case "webhook":
        return (
          <WebhookCapabilityForm
            form={form as UseFormReturn<z.infer<typeof WebhookCapabilityConfigSchema>>}
            activationSettings={activationSettings}
          />
        )

      case "instruction":
        return (
          <InstructionCapabilityForm
            form={form as UseFormReturn<z.infer<typeof InstructionCapabilityConfigSchema>>}
            activationSettings={activationSettings}
            requirements={instructionRequirements}
            requirementsAgentPrompt={requirementsAgentPrompt}
            onRequirementsChange={setInstructionRequirements}
            onRequirementsAgentPromptChange={setRequirementsAgentPrompt}
          />
        )

      case "mcp":
        return (
          <MCPCapabilityForm
            form={form as UseFormReturn<z.infer<typeof McpCapabilityConfigSchema>>}
            activationSettings={activationSettings}
            isLoading={isFormPending}
          />
        )
    }
  }

  return (
    <CapabilitySheetLayout
      open={open}
      onOpenChange={onOpenChange}
      badge={getBadge(capability.kind)}
      title={dialogTitle}
      description={dialogDescription}
    >
      <Form
        {...form}
        onSubmit={form.handleSubmit(handleFormSubmit)}
        errorTranslations={getErrorTranslations()}
        className="flex flex-col flex-1 min-h-0 gap-0"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
          {renderContent()}
        </div>
        <SheetFooter className="flex shrink-0 flex-row justify-between gap-2 border-t bg-background p-5">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("shared.cancel")}
          </Button>
          <Button type="submit" disabled={isFormPending}>
            {mode === "configure" ? t("shared.save") : t("shared.install")}
          </Button>
        </SheetFooter>
      </Form>
    </CapabilitySheetLayout>
  )
}

function getInstructionRequirementsForSave({
  inferredKind,
  config,
  requirements,
  requirementsAgentPrompt,
}: {
  inferredKind: "webhook" | "instruction" | "mcp"
  config: unknown
  requirements: GeneratedRequirement[]
  requirementsAgentPrompt: string | null
}) {
  if (inferredKind !== "instruction" || requirements.length === 0) return undefined

  const parsed = InstructionCapabilityConfigSchema.safeParse(config)
  if (!parsed.success || requirementsAgentPrompt !== parsed.data.agentPrompt) return undefined

  return requirements
}
